import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Conversation, Message } from '@/types';
import { conversationService } from '@/services/conversationService';

interface ConversationState {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: ConversationState = {
  conversations: [],
  currentConversation: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

// Cache duration: 30 seconds (prevent duplicate fetches)
const CACHE_DURATION = 30000;
let inFlightConversationsRequest: Promise<Conversation[]> | null = null;

export const fetchConversations = createAsyncThunk(
  'conversations/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const force = options?.force ?? false;
    const state = getState() as { conversations: ConversationState };
    const now = Date.now();

    // Return cached data if recent and not forced
    if (
      !force &&
      state.conversations.conversations.length > 0 &&
      state.conversations.lastFetched &&
      now - state.conversations.lastFetched < CACHE_DURATION
    ) {
      return state.conversations.conversations;
    }

    try {
      if (!inFlightConversationsRequest) {
        inFlightConversationsRequest = conversationService
          .getConversations({ pageSize: 200 })
          .then(response => {
            if (response && Array.isArray((response as any).data)) {
              return (response as any).data as Conversation[];
            }
            if (Array.isArray(response)) {
              return response as any;
            }
            return [];
          })
          .finally(() => {
            inFlightConversationsRequest = null;
          });
      }

      return await inFlightConversationsRequest;
    } catch (error: any) {
      inFlightConversationsRequest = null;
      return rejectWithValue(error.message || 'Failed to fetch conversations');
    }
  }
);

export const applyMailboxConversationAction = createAsyncThunk(
  'conversations/applyMailboxAction',
  async (
    { id, action }: { id: string; action: 'read' | 'unread' | 'archived' | 'deleted' | 'restore' },
    { getState }
  ): Promise<{ id: string; isRead: boolean } | { id: string; status: Conversation['status'] }> => {
    const state = getState() as { conversations: ConversationState };
    const conversation = state.conversations.conversations.find(item => item.id === id);
    if (!conversation) throw new Error('Conversation no longer exists');
    if (action === 'read' || action === 'unread') {
      const isRead = action === 'read';
      await conversationService.updateConversation(id, {
        metadata: { ...conversation.metadata, isRead },
      });
      return { id, isRead };
    }
    let status: Conversation['status'] = 'open';
    if (action === 'archived') {
      await conversationService.archiveConversation(id);
      status = 'archived';
    } else if (action === 'deleted') {
      await conversationService.trashConversation(id);
      status = 'deleted';
    } else {
      await conversationService.updateConversation(id, { status });
    }
    return { id, status };
  }
);

const conversationSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setConversations: (state, action: PayloadAction<Conversation[]>) => {
      state.conversations = action.payload;
      state.isLoading = false;
    },
    setCurrentConversation: (state, action: PayloadAction<Conversation | null>) => {
      state.currentConversation = action.payload;
      state.isLoading = false;
    },
    addConversation: (state, action: PayloadAction<Conversation>) => {
      state.conversations.unshift(action.payload);
    },
    updateConversation: (state, action: PayloadAction<Conversation>) => {
      const index = state.conversations.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.conversations[index] = action.payload;
      }
      if (state.currentConversation?.id === action.payload.id) {
        state.currentConversation = action.payload;
      }
    },
    addMessage: (state, action: PayloadAction<{ conversationId: string; message: Message }>) => {
      const { conversationId, message } = action.payload;
      const conversation = state.conversations.find(c => c.id === conversationId);
      if (conversation) {
        conversation.messages.push(message);
        conversation.updatedAt = new Date().toISOString();
      }
      if (state.currentConversation?.id === conversationId) {
        state.currentConversation.messages.push(message);
        state.currentConversation.updatedAt = new Date().toISOString();
      }
    },
    deleteConversation: (state, action: PayloadAction<string>) => {
      state.conversations = state.conversations.filter(c => c.id !== action.payload);
      if (state.currentConversation?.id === action.payload) {
        state.currentConversation = null;
      }
    },
    updateConversationStatus: (
      state,
      action: PayloadAction<{ id: string; status: Conversation['status'] }>
    ) => {
      const idx = state.conversations.findIndex(c => c.id === action.payload.id);
      if (idx !== -1) {
        state.conversations[idx] = { ...state.conversations[idx], status: action.payload.status };
      }
      if (state.currentConversation?.id === action.payload.id) {
        state.currentConversation = { ...state.currentConversation, status: action.payload.status };
      }
    },
    updateConversationMetadata: (
      state,
      action: PayloadAction<{ id: string; metadata: Record<string, unknown> }>
    ) => {
      const idx = state.conversations.findIndex(c => c.id === action.payload.id);
      if (idx !== -1) {
        state.conversations[idx] = {
          ...state.conversations[idx],
          metadata: { ...state.conversations[idx].metadata, ...action.payload.metadata },
        };
      }
      if (state.currentConversation?.id === action.payload.id) {
        state.currentConversation = {
          ...state.currentConversation,
          metadata: { ...state.currentConversation.metadata, ...action.payload.metadata },
        };
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearConversations: state => {
      state.conversations = [];
      state.currentConversation = null;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(applyMailboxConversationAction.fulfilled, (state, action) => {
        const { payload } = action;
        [...state.conversations, state.currentConversation].forEach(conversation => {
          if (!conversation || conversation.id !== payload.id) return;
          if ('isRead' in payload) {
            conversation.metadata = { ...conversation.metadata, isRead: payload.isRead };
          } else {
            conversation.status = payload.status;
          }
        });
      })
      .addCase(applyMailboxConversationAction.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to update email';
      });
    builder
      .addCase(fetchConversations.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        const incoming = action.payload as Conversation[];
        state.conversations = incoming;
        if (
          state.currentConversation &&
          !incoming.some(conversation => conversation.id === state.currentConversation?.id)
        ) {
          state.currentConversation = null;
        }
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setConversations,
  setCurrentConversation,
  addConversation,
  updateConversation,
  addMessage,
  deleteConversation,
  updateConversationStatus,
  updateConversationMetadata,
  setLoading,
  setError,
  clearConversations,
} = conversationSlice.actions;
export default conversationSlice.reducer;
